/**
 *  @class
 *  @function BackgroundVideo
 */
if (!customElements.get('background-video')) {
  class BackgroundVideo extends HTMLElement {
    constructor() {
      super();

      this.tl = false;
      this.splittext = false;
      this.paused = false;
      this.toggle = this.querySelector('.background-video__controls button');
      this.playOverlay = this.querySelector('[data-play-overlay]');
      this.autoplay = this.querySelector('.background-video__iframe').dataset.autoplay === 'true';
      this.fallbackImage = this.querySelector('.background-video__fallback-img');
      this.videoControls = this.querySelector('.background-video__controls');
      this.videoStarted = false;
      this.youtubeComponent = this.querySelector('youtube-video');
      this.isYouTube = !!this.youtubeComponent;
      this.fullscreenToggle = this.querySelector('.background-video__fullscreen-toggle');
      this.isFullscreen = false;
    }
    connectedCallback() {
      let _this = this;
      if (document.body.classList.contains('animations-true') && typeof gsap !== 'undefined') {
        this.prepareAnimations();
      }
      // Video Support.
      this.video_container = this.querySelector('.background-video__iframe');

      if (this.video_container.querySelector('iframe')) {
        this.video_container.querySelector('iframe').onload = function() {
          if (_this.autoplay) {
            _this.startVideo();
            _this.videoPlay();
          }
        };
      }

      // Hide controls initially when autoplay is disabled
      if (this.videoControls && !this.autoplay) {
        this.videoControls.classList.add('background-video__controls--hidden');
      }

      // Play overlay click handler
      if (this.playOverlay && !this.autoplay) {
        this.playOverlay.addEventListener('click', this.handlePlayOverlayClick.bind(this));
      }

      // Controls.
      if (this.toggle) {
        this.toggle.addEventListener('click', this.setupControlEvents.bind(this));
      }

      // Fullscreen controls (only for non-YouTube videos)
      if (this.fullscreenToggle && !this.isYouTube) {
        this.fullscreenToggle.addEventListener('click', this.toggleFullscreen.bind(this));
        
        // Listen for fullscreen changes
        document.addEventListener('fullscreenchange', this.handleFullscreenChange.bind(this));
        document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange.bind(this));
        document.addEventListener('mozfullscreenchange', this.handleFullscreenChange.bind(this));
        document.addEventListener('MSFullscreenChange', this.handleFullscreenChange.bind(this));
      }
    }
    disconnectedCallback() {
      if (document.body.classList.contains('animations-true') && typeof gsap !== 'undefined') {
        this.tl.kill();
        this.splittext.revert();
      }
    }
    handlePlayOverlayClick() {
      this.startVideo();
      // Ensure iframe is loaded before playing
      this.ensureIframeLoaded().then(() => {
        setTimeout(() => {
          this.videoPlay();
        }, 100);
      });
      if (this.playOverlay) {
        this.playOverlay.classList.add('hidden');
      }
    }
    ensureIframeLoaded() {
      return new Promise((resolve) => {
        const iframe = this.video_container.querySelector('iframe');
        if (!iframe) {
          resolve();
          return;
        }

        // If iframe is already loaded
        if (iframe.src && iframe.src !== '') {
          resolve();
          return;
        }

        // If iframe has data-src (lazy loading), load it
        if (iframe.dataset.src) {
          iframe.src = iframe.dataset.src;
          iframe.onload = () => {
            resolve();
          };
        } else {
          resolve();
        }
      });
    }
    startVideo() {
      if (!this.videoStarted) {
        this.videoStarted = true;
        // Ensure CSS no longer hides the iframe when autoplay was initially disabled
        if (this.video_container) {
          this.video_container.setAttribute('data-autoplay', 'true');
        }
        // Hide fallback image and show video
        if (this.fallbackImage) {
          this.fallbackImage.classList.add('background-video__fallback-img--hidden');
        }
        if (this.video_container) {
          this.video_container.classList.add('background-video__iframe--visible');
        }
        // Show video controls
        if (this.videoControls && !this.autoplay) {
          this.videoControls.classList.remove('background-video__controls--hidden');
          this.videoControls.classList.add('background-video__controls--visible');
        }
      }
    }
    setupControlEvents() {
      if (this.paused) {
        this.videoPlay();
        if (this.toggle) {
          this.toggle.classList.remove('paused');
        }
      } else {
        this.videoPause();
        if (this.toggle) {
          this.toggle.classList.add('paused');
        }
      }
    }
    videoPlay() {
      setTimeout(() => {
        try {
          if (this.isYouTube && this.youtubeComponent && this.youtubeComponent.player) {
            // Use YouTube component API
            this.youtubeComponent.player.playVideo();
          } else if (this.video_container.dataset.provider === 'youtube') {
            const iframe = this.video_container.querySelector('iframe');
            if (iframe && iframe.contentWindow) {
              iframe.contentWindow.postMessage(JSON.stringify({
                event: "command",
                func: "playVideo",
                args: ""
              }), "*");
            }
          } else if (this.video_container.dataset.provider === 'vimeo') {
            const iframe = this.video_container.querySelector('iframe');
            if (iframe && iframe.contentWindow) {
              iframe.contentWindow.postMessage(JSON.stringify({
                method: "play"
              }), "*");
            }
          } else if (this.video_container.dataset.provider === 'hosted') {
            const video = this.video_container.querySelector('video');
            if (video) {
              video.play();
            }
          }
          this.paused = false;
        } catch (error) {
          // Error handling for video play
        }
      }, 10);
    }
    videoPause() {
      setTimeout(() => {
        try {
          if (this.isYouTube && this.youtubeComponent && this.youtubeComponent.player) {
            // Use YouTube component API
            this.youtubeComponent.player.pauseVideo();
          } else if (this.video_container.dataset.provider === 'youtube') {
            const iframe = this.video_container.querySelector('iframe');
            if (iframe && iframe.contentWindow) {
              iframe.contentWindow.postMessage(JSON.stringify({
                event: "command",
                func: "pauseVideo",
                args: ""
              }), "*");
            }
          } else if (this.video_container.dataset.provider === 'vimeo') {
            const iframe = this.video_container.querySelector('iframe');
            if (iframe && iframe.contentWindow) {
              iframe.contentWindow.postMessage(JSON.stringify({
                method: "pause"
              }), "*");
            }
          } else if (this.video_container.dataset.provider === 'hosted') {
            const video = this.video_container.querySelector('video');
            if (video) {
              video.pause();
            }
          }
          this.paused = true;
        } catch (error) {
          // Error handling for video pause
        }
      }, 10);
    }
    prepareAnimations() {
      let section = this,
        button_offset = 0;

      section.tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top center"
        }
      });

      document.fonts.ready.then(function() {
        section.splittext = new SplitText(section.querySelectorAll('h3, p:not(.subheading)'), {
          type: 'lines',
          linesClass: 'line-child'
        });
        section.mask = new SplitText(section.querySelectorAll('h3, p:not(.subheading)'), {
          type: 'lines',
          linesClass: 'line-parent'
        });

        if (section.querySelector('.subheading')) {
          section.tl
            .fromTo(section.querySelector('.subheading'), {
              opacity: 0
            }, {
              duration: 0.75,
              opacity: 0.6
            }, 0);

          button_offset += 0.5;
        }
        if (section.querySelector('h3')) {
          let h3_duration = 0.7 + ((section.querySelectorAll('h3 .line-child').length - 1) * 0.05);
          section.tl
            .from(section.querySelectorAll('h3 .line-child'), {
              duration: h3_duration,
              yPercent: 120,
              stagger: 0.05,
              rotation: '3deg'
            }, 0);
          button_offset += h3_duration;
        }
        if (section.querySelector('.rte p')) {
          let p_duration = 0.7 + ((section.querySelectorAll('p .line-child').length - 1) * 0.02);
          section.tl
            .set(section.querySelectorAll('.rte p'), {
              visibility: 'visible'
            }, 0)
            .from(section.querySelectorAll('.rte p .line-child'), {
              duration: p_duration,
              yPercent: 120,
              stagger: 0.02,
              rotation: '3deg'
            }, 0);
          button_offset += p_duration;
        }
        if (section.querySelector('.video-lightbox-modal__button')) {
          section.tl
            .fromTo(section.querySelector('.video-lightbox-modal__button'), {
              autoAlpha: 0
            }, {
              duration: 0.5,
              autoAlpha: 1
            }, button_offset * 0.4);
        }

      });
    }
    toggleFullscreen() {
      if (this.isFullscreen) {
        this.exitFullscreen();
      } else {
        this.enterFullscreen();
      }
    }
    enterFullscreen() {
      // Check if we're on iOS Safari (which requires video element fullscreen)
      const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && 
                         !window.MSStream && 
                         'webkitEnterFullscreen' in document.createElement('video');
      
      if (isIOSSafari) {
        // iOS Safari: Use video element fullscreen
        const videoElement = this.video_container.querySelector('video');
        const youtubeIframe = this.video_container.querySelector('iframe');
        
        if (videoElement && videoElement.webkitEnterFullscreen) {
          videoElement.webkitEnterFullscreen();
          return;
        } else if (youtubeIframe && this.isYouTube) {
          // For YouTube on iOS, try to trigger the iframe's fullscreen
          // YouTube's player will handle the fullscreen natively
          if (youtubeIframe.webkitEnterFullscreen) {
            youtubeIframe.webkitEnterFullscreen();
          }
          return;
        }
      }
      
      // Desktop/Modern browsers: Make the entire container fullscreen
      const targetElement = this;
      
      if (targetElement.requestFullscreen) {
        targetElement.requestFullscreen().catch(err => {
          console.error('Fullscreen request failed:', err);
        });
      } else if (targetElement.webkitRequestFullscreen) {
        targetElement.webkitRequestFullscreen();
      } else if (targetElement.mozRequestFullScreen) {
        targetElement.mozRequestFullScreen();
      } else if (targetElement.msRequestFullscreen) {
        targetElement.msRequestFullscreen();
      }
    }
    exitFullscreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
    handleFullscreenChange() {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      
      this.isFullscreen = isCurrentlyFullscreen;
      
      if (this.fullscreenToggle) {
        if (this.isFullscreen) {
          this.fullscreenToggle.classList.add('fullscreen-active');
          this.setupFullscreenControls();
        } else {
          this.fullscreenToggle.classList.remove('fullscreen-active');
          this.cleanupFullscreenControls();
        }
      }
    }

    setupFullscreenControls() {
      // Check if we're on iOS Safari
      const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && 
                         !window.MSStream;
      
      if (isIOSSafari) {
        // iOS Safari handles video fullscreen natively, no custom controls needed
        return;
      }
      
      // Desktop/Modern browsers: Show controls temporarily when entering fullscreen
      if (this.videoControls) {
        this.videoControls.style.opacity = '1';
        
        // Hide controls after 3 seconds of no mouse movement
        this.hideControlsTimeout = setTimeout(() => {
          if (this.isFullscreen) {
            this.videoControls.style.opacity = '0';
          }
        }, 3000);
      }

      // Add mouse movement listener
      this.mouseMoveHandler = () => {
        if (this.isFullscreen && this.videoControls) {
          this.videoControls.style.opacity = '1';
          
          // Clear existing timeout
          if (this.hideControlsTimeout) {
            clearTimeout(this.hideControlsTimeout);
          }
          
          // Set new timeout to hide controls
          this.hideControlsTimeout = setTimeout(() => {
            if (this.isFullscreen) {
              this.videoControls.style.opacity = '0';
            }
          }, 3000);
        }
      };

      document.addEventListener('mousemove', this.mouseMoveHandler);
    }

    cleanupFullscreenControls() {
      // Remove mouse movement listener
      if (this.mouseMoveHandler) {
        document.removeEventListener('mousemove', this.mouseMoveHandler);
        this.mouseMoveHandler = null;
      }

      // Clear timeout
      if (this.hideControlsTimeout) {
        clearTimeout(this.hideControlsTimeout);
        this.hideControlsTimeout = null;
      }

      // Reset controls opacity
      if (this.videoControls) {
        this.videoControls.style.opacity = '';
      }
    }
  }
  customElements.define('background-video', BackgroundVideo);
}